import { Alert } from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useTimestampFormatActions } from "@/shared/components/RobotoMarkdown/timeFormat";
import {
  AgentThreadStatus,
  isAgentThreadTextContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { BROWSER_CLIENT_TOOLS } from "@/shared/domain/agentThread/clientTools";
import {
  ApprovalMode,
  resolveApprovalMode,
} from "@/shared/domain/agentThread/clientTools/approvalModes";
import { useActiveAgentThread } from "@/shared/domain/agentThread/hooks/useActiveAgentThread";
import { useCancelGeneration } from "@/shared/domain/agentThread/hooks/useCancelGeneration";
import { useClientToolExecution } from "@/shared/domain/agentThread/hooks/useClientToolExecution";
import { useClientToolPreferences } from "@/shared/domain/agentThread/hooks/useClientToolPreferences";
import { useSendMessage } from "@/shared/domain/agentThread/hooks/useSendMessage";
import { useServerViewWrites } from "@/shared/domain/agentThread/hooks/useServerViewWrites";
import { useStartAgentThread } from "@/shared/domain/agentThread/hooks/useStartAgentThread";
import { subscribePendingKickoff } from "@/shared/domain/agentThread/pendingKickoff";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./AgentThread.module.css";
import { AgentThreadByIdView } from "./AgentThreadByIdView";
import { AgentThreadInputBox } from "./AgentThreadInputBox";
import { useAIAgentThread } from "./AIAgentThreadContext";
import { ClientToolSettingsModal } from "./ClientToolSettingsModal";
import { NewAgentThreadView } from "./NewAgentThreadView";
import { withDisplayAnchor } from "./timeFormat/withDisplayAnchor";

interface AgentThreadProps {
  /**
   * Watch the thread but execute none of its client tools.
   *
   * Set by the thread detail page once a navigation has handed the thread to
   * the chat sidebar. Both copies are mounted for that moment and both see the
   * same pending batch; suspending the departing one gives the batch to the
   * sidebar, which survives the navigation, instead of to whichever executor
   * claims the tool_use ids first.
   */
  clientToolsSuspended?: boolean;
}

/**
 * AgentThread component orchestrates all data fetching and mutations.
 * It conditionally renders NewAgentThreadView vs AgentThreadByIdView based on
 * whether threadId exists. AgentThreadInputBox is rendered as a pure form
 * component with callbacks.
 */
export const AgentThread: React.FC<AgentThreadProps> = ({
  clientToolsSuspended = false,
}) => {
  const orgId = useCurrentOrgId();
  const {
    currentThreadId,
    navigateToThread,
    getContextSnapshot,
    modelProfile,
    autoEnabled,
    handleToggleAuto,
  } = useAIAgentThread();

  // Data fetching
  const agentThreadQuery = useActiveAgentThread(currentThreadId, {
    orgId,
    loadMessages: true,
  });
  const agentThread = agentThreadQuery.data;

  // Client-side tool preferences (read-only — mutation is in the settings modal)
  const { data: preferences } = useClientToolPreferences();

  // Client-side tool execution with approval gating
  const { pendingApproval, handleApprove, handleDecline } =
    useClientToolExecution(agentThread, orgId, autoEnabled, preferences, {
      suspended: clientToolsSuspended,
    });

  // View writes run on the server, so the page learns of them from the thread.
  useServerViewWrites(agentThread, orgId);

  // Whether to offer "turn on Auto" alongside the pending approval. Only
  // meaningful for a tool that follows the setting: one pinned to
  // REQUIRE_APPROVAL prompts either way, and offering to change that would be
  // a lie. `handleToggleAuto` rather than a setter because it owns the
  // sessionStorage write; the `autoEnabled` guard makes the toggle a turn-on.
  const canSkipNextTimeWithAuto =
    !autoEnabled &&
    pendingApproval !== null &&
    resolveApprovalMode(pendingApproval.tool, preferences) ===
      ApprovalMode.FOLLOW_AUTO;

  // Settings modal state (opened from confirmation modal gear icon)
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);

  // Mutations
  const startAgentThreadMutation = useStartAgentThread();
  const sendMessageMutation = useSendMessage();
  const cancelGenerationMutation = useCancelGeneration();

  // Cancellation pending state - cleared when backend confirms cancellation
  const [isCancelling, setIsCancelling] = React.useState(false);
  const isSubmittingRef = React.useRef(false);

  // Clear cancellation state when thread status changes to USER_TURN
  React.useEffect(() => {
    if (agentThread?.status === AgentThreadStatus.USER_TURN) {
      setIsCancelling(false);
    }
  }, [agentThread?.status]);

  // Derived state: the thread is "loading" when it exists and isn't the user's
  // turn, OR when the thread query is in its initial loading state (bridges the
  // gap between startAgentThread completing and the query resolving).
  const loading =
    agentThreadQuery.isLoading ||
    (agentThreadQuery.data !== undefined &&
      agentThreadQuery.data?.status !== AgentThreadStatus.USER_TURN);

  // The reader's t=0 and the name they see for it ride along with every send,
  // so a relative time they type resolves against the same origin their screen
  // is using and the agent can name it back to them. Read through the actions
  // context, and only at send time: a thread following the visualizer moves its
  // t=0 as the workspace loads other files, and subscribing to the format
  // itself would re-render the whole transcript — re-parsing every message's
  // markdown — each time the reader changed how timestamps look.
  const timestampFormatActions = useTimestampFormatActions();

  // Mutation handlers
  const handleStartAgentThread = (
    message: string,
    chosenModelProfile: string | undefined,
  ) => {
    // Use mutate + onSuccess instead of mutateAsync so React batches
    // the isPending→false and navigateToThread address updates in the
    // same render, preventing a flash of the send button.
    return new Promise<void>((resolve, reject) => {
      startAgentThreadMutation.mutate(
        {
          message,
          orgId,
          clientContext: withDisplayAnchor(
            getContextSnapshot(),
            timestampFormatActions?.getDisplayAnchor(),
          ),
          modelProfile: chosenModelProfile,
          clientTools: BROWSER_CLIENT_TOOLS,
        },
        {
          onSuccess: (agentThreadRecord) => {
            navigateToThread(agentThreadRecord.thread_id);
            resolve();
          },
          onError: (error) => reject(error),
        },
      );
    });
  };

  // Pending-kickoff handoff: an external caller (e.g. the "Build with
  // chat" CTA on the agents list) stashes a curated first user-message
  // via `stashPendingKickoff`. We start a fresh thread with that message
  // regardless of the user's previously-active thread — the kickoff is a
  // deliberate "start over with this prompt" signal, so it also ignores any
  // model the user picked for the thread it replaces: the new thread starts on
  // the org default.
  //
  // `subscribePendingKickoff` owns the dual-channel (storage latch +
  // window event) plumbing. The ref pattern here is what keeps the
  // subscription stable across the AgentThread's lifetime while still firing
  // against the latest `orgId`/`handleStartAgentThread` closure on each
  // invocation. The subscription's handler reads `runKickoffRef.current`
  // every time, so even though the effect runs once on mount, each
  // kickoff dispatches to the freshest closure.
  const runKickoffRef = React.useRef<(kickoff: string) => void>(() => {});
  React.useEffect(() => {
    runKickoffRef.current = (kickoff: string) => {
      navigateToThread(undefined);
      void handleStartAgentThread(kickoff, undefined);
    };
  });

  React.useEffect(
    () =>
      subscribePendingKickoff((message) => {
        runKickoffRef.current(message);
      }),
    // Subscribing exactly once is intentional: the ref-forwarding above
    // is how new closures reach the handler, so re-subscribing on every
    // `orgId`/`handleStartAgentThread` change would only churn listener
    // registrations without changing behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSendMessage = async (message: string) => {
    if (!currentThreadId) {
      return;
    }

    await sendMessageMutation.mutateAsync({
      threadId: currentThreadId,
      message: message,
      orgId,
      clientContext: withDisplayAnchor(
        getContextSnapshot(),
        timestampFormatActions?.getDisplayAnchor(),
      ),
      clientTools: BROWSER_CLIENT_TOOLS,
    });
  };

  // Callbacks for AgentThreadInput
  const handleSubmit = async (message: string): Promise<void> => {
    const shouldSendMessage =
      currentThreadId !== undefined && !sendMessageMutation.isPending;
    const shouldStartAgentThread =
      currentThreadId === undefined &&
      message.trim() !== "" &&
      !startAgentThreadMutation.isPending;

    if (
      isSubmittingRef.current ||
      (!shouldSendMessage && !shouldStartAgentThread)
    ) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      if (shouldSendMessage) {
        await handleSendMessage(message);
      } else {
        await handleStartAgentThread(message, modelProfile);
      }
    } catch (err) {
      ErrorMonitoringService.captureError(err);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleExampleClick = async (question: string): Promise<void> => {
    if (!startAgentThreadMutation.isPending) {
      await handleStartAgentThread(question, modelProfile);
    }
  };

  const handleCancel = async () => {
    // Reset mutations to cancel any pending requests
    startAgentThreadMutation.reset();
    sendMessageMutation.reset();

    // If we have an active thread that's generating, request cancellation from the backend
    if (
      currentThreadId &&
      agentThread?.status !== AgentThreadStatus.USER_TURN
    ) {
      setIsCancelling(true);
      try {
        await cancelGenerationMutation.mutateAsync({
          threadId: currentThreadId,
        });
      } catch (err) {
        setIsCancelling(false);
        ErrorMonitoringService.captureError(err);
      }
    }
  };

  const handleRetry = React.useCallback(
    (messageSequenceNum: number) => {
      if (!agentThread) {
        return;
      }

      const message = agentThread.messages[messageSequenceNum];
      const textContent = message?.content.find(isAgentThreadTextContent);

      if (textContent === undefined) {
        return;
      }

      sendMessageMutation.reset();
      void sendMessageMutation.retry(agentThread.thread_id, textContent.text);
    },
    [agentThread, sendMessageMutation],
  );

  const sending =
    startAgentThreadMutation.isPending || sendMessageMutation.isPending;
  const isGenerating = loading;

  return (
    <>
      <div className={styles.content}>
        {/* Error Alert */}
        {startAgentThreadMutation.isError && (
          <Alert
            severity="error"
            onClose={() => {
              startAgentThreadMutation.reset();
            }}
            className={styles.errorAlert}
          >
            {startAgentThreadMutation.error?.message || "An error occurred"}
          </Alert>
        )}
        {/* GOALS_FAILED banner — surfaces when the agent runner exhausts its
            corrective-reprompt budget without satisfying every declared goal. */}
        {agentThread?.status === AgentThreadStatus.GOALS_FAILED && (
          <Alert severity="error" className={styles.errorAlert}>
            The agent could not achieve all declared goals for this turn.
            Inspect the transcript to see which goals failed.
          </Alert>
        )}
        {/* Conditional Views */}
        <div className={styles.agentThreadView}>
          {agentThread ? (
            // Keyed by thread because this view holds state that means
            // something only for the conversation it was measured against:
            // scroll position, the first-run auto-scroll latch, an open jump
            // target. The provider above deliberately does not remount on a
            // thread change — that would tear down the client-tool executor
            // mid-batch — so the reset belongs here, below it.
            <AgentThreadByIdView
              key={agentThread.thread_id}
              agentThread={agentThread}
              isCancelling={isCancelling}
              onRetry={handleRetry}
              pendingApproval={pendingApproval}
              onApprove={() => void handleApprove()}
              onDecline={handleDecline}
              onEnableAutoMode={
                canSkipNextTimeWithAuto ? handleToggleAuto : undefined
              }
            />
          ) : (
            <NewAgentThreadView onExampleClick={handleExampleClick} />
          )}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <AgentThreadInputBox
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={sending || isGenerating}
          isCancelling={isCancelling}
          agentThreadModelProfile={agentThread?.model_profile}
          onAgentThreadCreatedFromSkill={(sessionId) =>
            navigateToThread(sessionId)
          }
        />
      </div>

      {/* Settings modal (can be opened from approval card or AutoToggle gear) */}
      <ClientToolSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
    </>
  );
};
